import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RentalUnitUser, CreateTenantApplicationData, TenantFilters } from '../../types';
import { tenantService } from '../../services/tenantService';

interface StatusCounts {
  all_tenants: number;
  active: number;
  pending: number;
  upcoming: number;
  notice_given: number;
  moved_out: number;
}

interface TenantsState {
  tenants: RentalUnitUser[];
  currentTenant: RentalUnitUser | null;
  tenantsByStatus: { [status: string]: RentalUnitUser[] };
  tenantsByCompany: { [companyId: string]: RentalUnitUser[] };
  tenantDocuments: { [tenantId: string]: any[] };
  vaultDocuments: any[];
  vaultDocumentsPagination: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
    search: string;
    sortOrder: 'asc' | 'desc';
  };
  isLoadingVaultDocuments: boolean;
  tenantPaymentHistory: { [tenantId: string]: any[] };
  tenantOutstandingDues: { [tenantId: string]: {
    rent: number;
    maintenance: number;
    utilities: number;
    penalties: number;
    total: number;
  } };
  tenantBehaviorRecord: { [tenantId: string]: any };
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  statusCounts: StatusCounts;
  filters: TenantFilters;
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isUploadingDocuments: boolean;
  error: string | null;
}

const initialState: TenantsState = {
  tenants: [],
  currentTenant: null,
  tenantsByStatus: {},
  tenantsByCompany: {},
  tenantDocuments: {},
  vaultDocuments: [],
  vaultDocumentsPagination: {
    page: 1,
    limit: 12,
    total: 0,
    hasMore: false,
    search: '',
    sortOrder: 'desc'
  },
  isLoadingVaultDocuments: false,
  tenantPaymentHistory: {},
  tenantOutstandingDues: {},
  tenantBehaviorRecord: {},
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  },
  statusCounts: {
    all_tenants: 0,
    active: 0,
    pending: 0,
    upcoming: 0,
    notice_given: 0,
    moved_out: 0
  },
  filters: {
    page: 1,
    limit: 10,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  },
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isUploadingDocuments: false,
  error: null
};

// Async thunks
export const fetchTenants = createAsyncThunk(
  'tenants/fetchTenants',
  async (filters?: TenantFilters) => {
    const response = await tenantService.getTenants(filters);
    return response;
  }
);

export const fetchTenant = createAsyncThunk(
  'tenants/fetchTenant',
  async (id: string) => {
    const response = await tenantService.getTenant(id);
    return response.data;
  }
);

export const createTenantApplication = createAsyncThunk(
  'tenants/createTenantApplication',
  async (data: CreateTenantApplicationData) => {
    const response = await tenantService.createTenantApplication(data);
    return response.data;
  }
);

export const updateTenantApplication = createAsyncThunk(
  'tenants/updateTenantApplication',
  async ({ id, data }: { id: string; data: any }, { rejectWithValue }) => {
    try {
      const response = await tenantService.updateTenantApplication(id, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || { message: error?.message });
    }
  }
);

export const approveTenantApplication = createAsyncThunk(
  'tenants/approveTenantApplication',
  async ({ id, data }: {
    id: string;
    data?: { approvalNotes?: string; moveInDate?: string }
  }) => {
    const response = await tenantService.approveTenantApplication(id, data);
    return response.data;
  }
);

export const rejectTenantApplication = createAsyncThunk(
  'tenants/rejectTenantApplication',
  async ({ id, rejectionReason }: { id: string; rejectionReason: string }) => {
    const response = await tenantService.rejectTenantApplication(id, rejectionReason);
    return response.data;
  }
);

export const updateTenantStatus = createAsyncThunk(
  'tenants/updateTenantStatus',
  async ({ id, status, isActive }: { id: string; status?: string; isActive?: boolean }) => {
    const response = await tenantService.updateTenantApplication(id, { status, isActive } as any);
    return response.data;
  }
);

export const updateTenantStatusWithUnit = createAsyncThunk(
  'tenants/updateTenantStatusWithUnit',
  async ({ id, status, moveOutDate, isActive, noticePeriodGiven }: { 
    id: string; 
    status: string; 
    moveOutDate?: string;
    isActive?: boolean;
    noticePeriodGiven?: string;
  }) => {
    const response = await tenantService.updateTenantStatusWithUnit(id, { 
      status, 
      moveOutDate, 
      isActive, 
      noticePeriodGiven 
    });
    return response.data;
  }
);

export const fetchTenantsByStatus = createAsyncThunk(
  'tenants/fetchTenantsByStatus',
  async ({ status, companyId }: { status: string; companyId?: string }) => {
    const response = await tenantService.getTenantsByStatus(status, companyId);
    return { status, tenants: response.data };
  }
);

export const fetchTenantsByCompany = createAsyncThunk(
  'tenants/fetchTenantsByCompany',
  async ({ companyId, filters }: { companyId: string; filters?: TenantFilters }) => {
    const response = await tenantService.getTenantsByCompany(companyId, filters);
    return { companyId, data: response };
  }
);

export const fetchTenantPaymentHistory = createAsyncThunk(
  'tenants/fetchTenantPaymentHistory',
  async (id: string) => {
    const response = await tenantService.getTenantPaymentHistory(id);
    return { tenantId: id, payments: response.data };
  }
);

export const fetchTenantOutstandingDues = createAsyncThunk(
  'tenants/fetchTenantOutstandingDues',
  async (id: string) => {
    const response = await tenantService.getTenantOutstandingDues(id);
    return { tenantId: id, dues: response.data };
  }
);

export const updateTenantFinancials = createAsyncThunk(
  'tenants/updateTenantFinancials',
  async ({ id, data }: {
    id: string;
    data: {
      depositAmount?: { paid: number; pending: number };
      outstandingDues?: {
        rent?: number;
        maintenance?: number;
        utilities?: number;
        penalties?: number;
      };
      lastRentPaid?: string;
    }
  }) => {
    const response = await tenantService.updateTenantFinancials(id, data);
    return response.data;
  }
);

export const addTenantNote = createAsyncThunk(
  'tenants/addTenantNote',
  async ({ id, note }: {
    id: string;
    note: { note: string; type: 'general' | 'important' | 'warning' }
  }) => {
    const response = await tenantService.addTenantNote(id, note);
    return response.data;
  }
);

export const updateTenantCompliance = createAsyncThunk(
  'tenants/updateTenantCompliance',
  async ({ id, data }: {
    id: string;
    data: {
      policeVerification?: {
        status: string;
        completedDate?: string;
        referenceNumber?: string;
      };
      backgroundCheck?: {
        status: string;
        completedDate?: string;
        score?: number;
      };
    }
  }) => {
    const response = await tenantService.updateTenantCompliance(id, data);
    return response.data;
  }
);

export const fetchTenantDocuments = createAsyncThunk(
  'tenants/fetchTenantDocuments',
  async (id: string) => {
    const response = await tenantService.getTenantDocuments(id);
    return { tenantId: id, documents: response.data };
  }
);

export const fetchDocumentVault = createAsyncThunk(
  'tenants/fetchDocumentVault',
  async ({
    userId,
    companyId,
    search,
    page,
    limit,
    sortOrder,
    append
  }: {
    userId: string;
    companyId: string;
    search?: string;
    page?: number;
    limit?: number;
    sortOrder?: 'asc' | 'desc';
    append?: boolean;
  }) => {
    const response = await tenantService.getDocumentVault(userId, companyId, {
      search,
      page,
      limit,
      sortOrder
    });
    return {
      documents: response.data?.documents || [],
      page: response.data?.page || 1,
      limit: response.data?.limit || limit || 12,
      total: response.data?.total || 0,
      hasMore: response.data?.hasMore || false,
      search: search || '',
      sortOrder: sortOrder || 'desc',
      append: Boolean(append)
    };
  }
);

export const uploadTenantDocument = createAsyncThunk(
  'tenants/uploadTenantDocument',
  async ({ id, file, type }: { id: string; file: File; type: string }) => {
    const response = await tenantService.uploadTenantDocument(id, file, type);
    return { tenantId: id, document: response.data };
  }
);

export const addTenantDocumentByUrl = createAsyncThunk(
  'tenants/addTenantDocumentByUrl',
  async ({ id, type, url, text }: { id: string; type: string; url?: string; text?: string }) => {
    const response = await tenantService.addTenantDocumentByUrl(id, { type, url, text });
    return { tenantId: id, document: response.data };
  }
);

export const verifyTenantDocument = createAsyncThunk(
  'tenants/verifyTenantDocument',
  async ({ id, documentId, verified }: {
    id: string;
    documentId: string;
    verified: boolean;
  }) => {
    await tenantService.verifyTenantDocument(id, documentId, verified);
    return { tenantId: id, documentId, verified };
  }
);

export const deleteTenantDocument = createAsyncThunk(
  'tenants/deleteTenantDocument',
  async ({ id, documentId }: { id: string; documentId: string }) => {
    await tenantService.deleteTenantDocument(id, documentId);
    return { tenantId: id, documentId };
  }
);

export const fetchTenantBehaviorRecord = createAsyncThunk(
  'tenants/fetchTenantBehaviorRecord',
  async (id: string) => {
    const response = await tenantService.getTenantBehaviorRecord(id);
    return { tenantId: id, behaviorRecord: response.data };
  }
);

export const addTenantComplaint = createAsyncThunk(
  'tenants/addTenantComplaint',
  async ({ id, complaint }: {
    id: string;
    complaint: { description: string; severity: 'low' | 'medium' | 'high' }
  }) => {
    const response = await tenantService.addTenantComplaint(id, complaint);
    return response.data;
  }
);

export const addTenantWarning = createAsyncThunk(
  'tenants/addTenantWarning',
  async ({ id, warning }: { id: string; warning: { reason: string } }) => {
    const response = await tenantService.addTenantWarning(id, warning);
    return response.data;
  }
);

export const updateTenantRating = createAsyncThunk(
  'tenants/updateTenantRating',
  async ({ id, ratings }: {
    id: string;
    ratings: {
      cleanliness?: number;
      cooperation?: number;
      timelyPayment?: number;
      overallRating?: number;
    }
  }) => {
    const response = await tenantService.updateTenantRating(id, ratings);
    return response.data;
  }
);

export const moveTenantOut = createAsyncThunk(
  'tenants/moveTenantOut',
  async ({ id, data }: {
    id: string;
    data: {
      moveOutDate: string;
      reason?: string;
      finalSettlement?: {
        depositRefund: number;
        outstandingCharges: number;
        damages: number;
      };
    }
  }) => {
    const response = await tenantService.moveTenantOut(id, data);
    return response.data;
  }
);

export const extendTenantLease = createAsyncThunk(
  'tenants/extendTenantLease',
  async ({ id, data }: {
    id: string;
    data: {
      newEndDate: string;
      rentIncrease?: number;
      terms?: string;
    }
  }) => {
    const response = await tenantService.extendTenantLease(id, data);
    return response.data;
  }
);

export const transferTenant = createAsyncThunk(
  'tenants/transferTenant',
  async ({ id, data }: {
    id: string;
    data: {
      newUnitId: string;
      transferDate?: string;
      reason?: string;
      newAgreementDetails?: {
        rentAmount?: number;
        securityDeposit?: number;
        maintenanceCharges?: number;
        rentDueDate?: number;
      };
      depositHandling?: 'transfer' | 'settle_and_collect' | 'adjust_difference';
      generateFinalInvoice?: boolean;
      finalMeterReadings?: {
        electricity?: number;
      };
      newPrimaryTenantId?: string;
    }
  }) => {
    const response = await tenantService.transferTenant(id, data);
    return response.data;
  }
);

const tenantsSlice = createSlice({
  name: 'tenants',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<TenantFilters>) => {
      state.filters = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetTenants: (state) => {
      state.tenants = [];
      state.currentTenant = null;
      state.tenantsByStatus = {};
      state.tenantsByCompany = {};
      state.tenantDocuments = {};
      state.tenantPaymentHistory = {};
      state.tenantOutstandingDues = {};
      state.tenantBehaviorRecord = {};
      state.pagination = initialState.pagination;
    },
    setCurrentTenant: (state, action: PayloadAction<RentalUnitUser | null>) => {
      state.currentTenant = action.payload;
    }
  },
  extraReducers: (builder) => {
    // Fetch tenants
    builder
      .addCase(fetchTenants.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTenants.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tenants = action.payload.data;
        if (action.payload.pagination) {
          state.pagination = {
            page: action.payload.pagination.page,
            limit: action.payload.pagination.limit,
            total: action.payload.pagination.totalCount,
            pages: action.payload.pagination.totalPages
          };
        }
        // Cast to any to access statusCounts from extended API response
        const payload = action.payload as any;
        if (payload.statusCounts) {
          state.statusCounts = payload.statusCounts;
        }
      })
      .addCase(fetchTenants.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch tenants';
      })
      
      // Fetch single tenant
      .addCase(fetchTenant.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTenant.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.currentTenant = action.payload;
        }
      })
      .addCase(fetchTenant.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch tenant';
      })
      
      // Create tenant application
      .addCase(createTenantApplication.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createTenantApplication.fulfilled, (state, action) => {
        state.isCreating = false;
        if (action.payload) {
          state.tenants.unshift(action.payload);
        }
      })
      .addCase(createTenantApplication.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.error.message || 'Failed to create tenant application';
      })
      
      // Update tenant application
      .addCase(updateTenantApplication.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateTenantApplication.fulfilled, (state, action) => {
        state.isUpdating = false;
        const tenant = action.payload;
        if (tenant) {
          const index = state.tenants.findIndex(t => t._id === tenant._id);
          if (index !== -1) {
            state.tenants[index] = tenant;
          }
          if (state.currentTenant?._id === tenant._id) {
            state.currentTenant = tenant;
          }
        }
      })
      .addCase(updateTenantApplication.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.error.message || 'Failed to update tenant application';
      })
      
      // Approve tenant application
      .addCase(approveTenantApplication.fulfilled, (state, action) => {
        const tenant = action.payload;
        if (tenant) {
          const index = state.tenants.findIndex(t => t._id === tenant._id);
          if (index !== -1) {
            state.tenants[index] = tenant;
          }
          if (state.currentTenant?._id === tenant._id) {
            state.currentTenant = tenant;
          }
        }
      })
      .addCase(approveTenantApplication.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to approve tenant application';
      })
      
      // Reject tenant application
      .addCase(rejectTenantApplication.fulfilled, (state, action) => {
        const tenant = action.payload;
        if (tenant) {
          const index = state.tenants.findIndex(t => t._id === tenant._id);
          if (index !== -1) {
            state.tenants[index] = tenant;
          }
          if (state.currentTenant?._id === tenant._id) {
            state.currentTenant = tenant;
          }
        }
      })
      .addCase(rejectTenantApplication.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to reject tenant application';
      })
      
      // Fetch tenants by status
      .addCase(fetchTenantsByStatus.fulfilled, (state, action) => {
        if (action.payload?.tenants) {
          state.tenantsByStatus[action.payload.status] = action.payload.tenants;
        }
      })
      .addCase(fetchTenantsByStatus.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch tenants by status';
      })
      
      // Fetch tenants by company
      .addCase(fetchTenantsByCompany.fulfilled, (state, action) => {
        state.tenantsByCompany[action.payload.companyId] = action.payload.data.data;
      })
      .addCase(fetchTenantsByCompany.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch tenants by company';
      })
      
      // Fetch tenant payment history
      .addCase(fetchTenantPaymentHistory.fulfilled, (state, action) => {
        if (action.payload?.payments) {
          state.tenantPaymentHistory[action.payload.tenantId] = action.payload.payments;
        }
      })
      
      // Fetch tenant outstanding dues
      .addCase(fetchTenantOutstandingDues.fulfilled, (state, action) => {
        if (action.payload?.dues) {
          state.tenantOutstandingDues[action.payload.tenantId] = action.payload.dues;
        }
      })
      
      // Update tenant financials
      .addCase(updateTenantFinancials.fulfilled, (state, action) => {
        const tenant = action.payload;
        if (tenant) {
          const index = state.tenants.findIndex(t => t._id === tenant._id);
          if (index !== -1) {
            state.tenants[index] = tenant;
          }
          if (state.currentTenant?._id === tenant._id) {
            state.currentTenant = tenant;
          }
        }
      })
      .addCase(updateTenantFinancials.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update tenant financials';
      })
      
      // Add tenant note
      .addCase(addTenantNote.fulfilled, (state, action) => {
        const tenant = action.payload;
        if (tenant) {
          const index = state.tenants.findIndex(t => t._id === tenant._id);
          if (index !== -1) {
            state.tenants[index] = tenant;
          }
          if (state.currentTenant?._id === tenant._id) {
            state.currentTenant = tenant;
          }
        }
      })
      
      // Update tenant compliance
      .addCase(updateTenantCompliance.fulfilled, (state, action) => {
        const tenant = action.payload;
        if (tenant) {
          const index = state.tenants.findIndex(t => t._id === tenant._id);
          if (index !== -1) {
            state.tenants[index] = tenant;
          }
          if (state.currentTenant?._id === tenant._id) {
            state.currentTenant = tenant;
          }
        }
      })
      
      // Fetch tenant documents
      .addCase(fetchTenantDocuments.fulfilled, (state, action) => {
        if (action.payload?.documents) {
          state.tenantDocuments[action.payload.tenantId] = action.payload.documents;
        }
      })
      
      // Fetch document vault
      .addCase(fetchDocumentVault.pending, (state) => {
        state.isLoadingVaultDocuments = true;
      })
      .addCase(fetchDocumentVault.fulfilled, (state, action) => {
        state.isLoadingVaultDocuments = false;
        if (action.payload.append) {
          state.vaultDocuments = [...state.vaultDocuments, ...action.payload.documents];
        } else {
          state.vaultDocuments = action.payload.documents || [];
        }
        state.vaultDocumentsPagination = {
          page: action.payload.page,
          limit: action.payload.limit,
          total: action.payload.total,
          hasMore: action.payload.hasMore,
          search: action.payload.search,
          sortOrder: action.payload.sortOrder
        };
      })
      .addCase(fetchDocumentVault.rejected, (state, action) => {
        state.isLoadingVaultDocuments = false;
        state.error = action.error.message || 'Failed to fetch documents';
      })
      
      // Upload tenant document
      .addCase(uploadTenantDocument.pending, (state) => {
        state.isUploadingDocuments = true;
        state.error = null;
      })
      .addCase(uploadTenantDocument.fulfilled, (state, action) => {
        state.isUploadingDocuments = false;
        const { tenantId, document } = action.payload;
        if (!state.tenantDocuments[tenantId]) {
          state.tenantDocuments[tenantId] = [];
        }
        state.tenantDocuments[tenantId].push(document);
      })
      .addCase(uploadTenantDocument.rejected, (state, action) => {
        state.isUploadingDocuments = false;
        state.error = action.error.message || 'Failed to upload document';
      })
      
      // Add tenant document by URL
      .addCase(addTenantDocumentByUrl.pending, (state) => {
        state.isUploadingDocuments = true;
        state.error = null;
      })
      .addCase(addTenantDocumentByUrl.fulfilled, (state, action) => {
        state.isUploadingDocuments = false;
        const updatedDocuments = action.payload.document?.documents;
        if (Array.isArray(updatedDocuments) && updatedDocuments.length > 0) {
          const latestDocument = updatedDocuments[updatedDocuments.length - 1];
          const activeSearch = state.vaultDocumentsPagination?.search?.toLowerCase().trim();
          const matchesSearch = !activeSearch ||
            [latestDocument?.type, latestDocument?.text]
              .filter(Boolean)
              .some((value) => String(value).toLowerCase().includes(activeSearch));

          if (!state.vaultDocumentsPagination?.page || (state.vaultDocumentsPagination.page === 1 && matchesSearch)) {
            state.vaultDocuments = [latestDocument, ...state.vaultDocuments];
            if (state.vaultDocumentsPagination) {
              state.vaultDocumentsPagination.total += 1;
              state.vaultDocumentsPagination.hasMore =
                state.vaultDocumentsPagination.page * state.vaultDocumentsPagination.limit <
                state.vaultDocumentsPagination.total;
            }
          }
        }
        // Also update currentTenant documents for backward compatibility
        if (state.currentTenant && state.currentTenant._id === action.payload.tenantId) {
          if (Array.isArray(updatedDocuments)) {
            state.currentTenant.documents = updatedDocuments;
          }
        }
      })
      .addCase(addTenantDocumentByUrl.rejected, (state, action) => {
        state.isUploadingDocuments = false;
        state.error = action.error.message || 'Failed to add document';
      })
      
      // Verify tenant document
      .addCase(verifyTenantDocument.fulfilled, (state, action) => {
        const { tenantId, documentId, verified } = action.payload;
        if (state.tenantDocuments[tenantId]) {
          const doc = state.tenantDocuments[tenantId].find(d => d._id === documentId);
          if (doc) {
            doc.verified = verified;
          }
        }
      })
      
      // Delete tenant document
      .addCase(deleteTenantDocument.fulfilled, (state, action) => {
        const { tenantId, documentId } = action.payload;
        if (state.tenantDocuments[tenantId]) {
          state.tenantDocuments[tenantId] = state.tenantDocuments[tenantId].filter(d => d._id !== documentId);
        }
      })
      
      // Fetch tenant behavior record
      .addCase(fetchTenantBehaviorRecord.fulfilled, (state, action) => {
        state.tenantBehaviorRecord[action.payload.tenantId] = action.payload.behaviorRecord;
      })
      
      // Add tenant complaint
      .addCase(addTenantComplaint.fulfilled, (state, action) => {
        const tenant = action.payload;
        if (tenant) {
          const index = state.tenants.findIndex(t => t._id === tenant._id);
          if (index !== -1) {
            state.tenants[index] = tenant;
          }
          if (state.currentTenant?._id === tenant._id) {
            state.currentTenant = tenant;
          }
        }
      })
      
      // Add tenant warning
      .addCase(addTenantWarning.fulfilled, (state, action) => {
        const tenant = action.payload;
        if (tenant) {
          const index = state.tenants.findIndex(t => t._id === tenant._id);
          if (index !== -1) {
            state.tenants[index] = tenant;
          }
          if (state.currentTenant?._id === tenant._id) {
            state.currentTenant = tenant;
          }
        }
      })
      
      // Update tenant rating
      .addCase(updateTenantRating.fulfilled, (state, action) => {
        const tenant = action.payload;
        if (tenant) {
          const index = state.tenants.findIndex(t => t._id === tenant._id);
          if (index !== -1) {
            state.tenants[index] = tenant;
          }
          if (state.currentTenant?._id === tenant._id) {
            state.currentTenant = tenant;
          }
        }
      })
      
      // Move tenant out
      .addCase(moveTenantOut.fulfilled, (state, action) => {
        const tenant = action.payload;
        if (tenant) {
          const index = state.tenants.findIndex(t => t._id === tenant._id);
          if (index !== -1) {
            state.tenants[index] = tenant;
          }
          if (state.currentTenant?._id === tenant._id) {
            state.currentTenant = tenant;
          }
        }
      })
      
      // Extend tenant lease
      .addCase(extendTenantLease.fulfilled, (state, action) => {
        const tenant = action.payload;
        if (tenant) {
          const index = state.tenants.findIndex(t => t._id === tenant._id);
          if (index !== -1) {
            state.tenants[index] = tenant;
          }
          if (state.currentTenant?._id === tenant._id) {
            state.currentTenant = tenant;
          }
        }
      })
    
      
      // Update tenant status
      .addCase(updateTenantStatus.fulfilled, (state, action) => {
        const tenant = action.payload;
        if (tenant) {
          const index = state.tenants.findIndex(t => t._id === tenant._id);
          if (index !== -1) {
            state.tenants[index] = tenant;
          }
          if (state.currentTenant?._id === tenant._id) {
            state.currentTenant = tenant;
          }
        }
      })
      
      // Update tenant status with unit
      .addCase(updateTenantStatusWithUnit.fulfilled, (state, action) => {
        if (action.payload?.tenant) {
          const { tenant } = action.payload;
          const index = state.tenants.findIndex(t => t._id === tenant._id);
          if (index !== -1) {
            state.tenants[index] = tenant;
          }
          if (state.currentTenant?._id === tenant._id) {
            state.currentTenant = tenant;
          }
        }
      });
  }
});

export const {
  setFilters,
  clearError,
  resetTenants,
  setCurrentTenant
} = tenantsSlice.actions;

export default tenantsSlice.reducer;
